/**
 * Audit Logger Utility
 * 
 * Provides audit logging for tracking security-related events
 * throughout the application.
 * 
 * @module utils/audit-logger
 */

/**
 * Audit logging for security and business events
 * 
 * Logs events with timestamps and context for debugging and compliance.
 * In production, these logs should be sent to a logging service.
 */
export const auditLog = {
  /**
   * Log a security or business event
   * 
   * @param event - Event name (e.g., 'user.login', 'error.boundary')
   * @param details - Additional context about the event
   * 
   * @example
   * auditLog.log('user.login', { userId: '123', method: 'oauth' });
   * auditLog.log('error.boundary', { error: error.message, component: 'App' });
   */
  log(event: string, details?: Record<string, unknown>): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      details,
      userAgent: navigator.userAgent,
    };
    
    // In production, send to a logging service (e.g., Sentry, LogRocket, DataDog). No such endpoint exists yet;
    // console.log is stripped from production builds (rspack.config.cjs, unless KEEP_CONSOLE=true).
    // Not copied to sessionStorage any more (security report F9): nothing read it, and any script on the
    // origin could — error-boundary entries carry error text.
    console.log('[AUDIT LOG]', logEntry);
  },

  /**
   * Log security-related events (alias for log with security prefix)
   * 
   * @param event - Security event name
   * @param details - Additional context about the event
   * 
   * @example
   * auditLog.security('ERROR_BOUNDARY_TRIGGERED', { error: error.message });
   */
  security(event: string, details?: Record<string, unknown>): void {
    this.log(`SECURITY:${event}`, details);
  },

  /**
   * Retrieve all stored audit logs
   * 
   * @returns Array of log entries
   */
  getLogs(): Array<{ 
    timestamp: string; 
    event: string; 
    details?: Record<string, unknown>;
    userAgent: string;
  }> {
    try {
      return JSON.parse(sessionStorage.getItem('audit_logs') || '[]');
    } catch {
      return [];
    }
  },

  /**
   * Clear all stored audit logs
   */
  clearLogs(): void {
    sessionStorage.removeItem('audit_logs');
  }
};
